import React, { useEffect, useState } from 'react';
import { Form, FormInstance } from 'antd';
import {
  DatePicker,
  Input,
  Modal,
  Radio,
  Select,
  Spinner,
} from 'UI';
import { UnitMapping } from 'constants/unit';
import { Client, Resource } from 'constants/interface';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanResourcesAction,
  getResourceListAction,
  isLoadingResourcesSelector,
  resourcesEntitiesSelector,
  resourcesIdsSelector,
} from 'state/slices/resource';
import { InventoryTypeEnum, PriceTypeEnum } from 'constants/inventory';
import {
  cleanClientsAction,
  clientsEntitiesSelector,
  clientsIdsSelector,
  getClientsListAction,
  isLoadingClientsSelector,
} from 'state/slices/clients';
import { updateEntryPrices } from 'helpers/inventory';
import dayjs, { Dayjs } from 'dayjs';
import { userPermissionsSelector } from 'state/slices/session';
import { PlusOutlined } from '@ant-design/icons';
import { ResourceCategoryEnum } from 'constants/resource';

import {
  FormContainer,
  BaseFormItem,
  InlineButton,
  RemoteSelectContent,
  RemoteSelectSpinnerContainer,
  QuantityFormItem,
  PriceFormItem,
  NotesInline,
  EntryItemFormRow,
  AddEntryItemButton,
  EntryItemInput,
  RemoveEntryItemButton,
  RemoveEntryItemIcon,
} from './style';

interface EntryItemFormData {
  resource: string
  quantity: string
  unitPrice: string
  totalPrice: string
}
export interface EntryTransferFormData {
  entryDate: Dayjs
  client: string
  price: string
  priceType: PriceTypeEnum
  destinationOrganization: string
  destinationClient: string
  destinationType: InventoryTypeEnum
  notes: string
  items: EntryItemFormData[]
}

interface EntryTransferModalProps {
  formHandler: FormInstance
  onSubmit(data: unknown): void
  onCancel(): void
  isLoading: boolean
  isVisible: boolean
  resourceCategory: ResourceCategoryEnum
}

const EntryTransferModal = ({
  formHandler,
  onSubmit,
  onCancel,
  isLoading,
  isVisible,
  resourceCategory,
}: EntryTransferModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const resourceSearchEntities = useSelector(resourcesEntitiesSelector);
  const resourceSearchIds = useSelector(resourcesIdsSelector);
  const isLoadingResources = useSelector(isLoadingResourcesSelector);
  const resourceSearchResult = resourceSearchIds
    .map((resourceId) => resourceSearchEntities[resourceId])
    .filter((resource) => !!resource) as Resource[];
  const [searchIndex, setSearchIndex] = useState<number>();

  const userPermissions = useSelector(userPermissionsSelector) || [];
  const organizations = userPermissions?.map((perm) => perm.organization);

  const clientsEntities = useSelector(clientsEntitiesSelector);
  const clientsIds = useSelector(clientsIdsSelector);
  const isLoadingClients = useSelector(isLoadingClientsSelector);
  const clients = clientsIds
    .map((clientId) => clientsEntities[clientId])
    .filter((client) => !!client) as Client[];

  const [internalLoading, setInternalLoading] = useState(false);

  const [
    resourceSearchTimeout,
    setResourceSearchTimeout,
  ] = useState<number>();
  const onResourceSearch = (search: string, index: number) => {
    if (!search) {
      dispatch(cleanResourcesAction());
      setSearchIndex(undefined);
    } else {
      setInternalLoading(true);
      setSearchIndex(index);
      window.clearTimeout(resourceSearchTimeout);
      const searchTimeout = window.setTimeout(() => {
        dispatch(getResourceListAction({
          search,
          isPaginated: false,
          category: resourceCategory,
        }));
        setInternalLoading(false);
      }, 500);
      setResourceSearchTimeout(searchTimeout);
    }
  };

  const [
    clientSearchTimeout,
    setClientSearchTimeout,
  ] = useState<number>();
  const onClientSearch = (search: string) => {
    if (!search) {
      dispatch(cleanClientsAction());
    } else {
      setInternalLoading(true);
      window.clearTimeout(clientSearchTimeout);
      const searchTimeout = window.setTimeout(() => {
        dispatch(getClientsListAction({
          search,
          isPaginated: false,
        }));
        setInternalLoading(false);
      }, 500);
      setClientSearchTimeout(searchTimeout);
    }
  };

  useEffect(() => {
    const clearEffects = () => {
      window.clearTimeout(resourceSearchTimeout);
      window.clearTimeout(clientSearchTimeout);
    };
    return clearEffects;
  }, []);

  const [selectedResources, setSelectedResources] = useState<(Resource | undefined)[]>([]);
  const [
    destinationType,
    setDestinationType,
  ] = useState<InventoryTypeEnum>(InventoryTypeEnum.ORGANIZATION);

  const internalOnCancel = () => {
    setSelectedResources([]);
    dispatch(cleanResourcesAction());
    onCancel();
  };

  const onEntryItemAdded = (
    formAdd: (defaultValue?: any, index?: number) => void, // eslint-disable-line
    index: number,
  ) => {
    const updatedSelectedResources = [
      ...selectedResources.slice(0, index),
      undefined,
      ...selectedResources.slice(index + 1),
    ];
    setSelectedResources(updatedSelectedResources);
    formAdd({
      resourceId: undefined,
    }, index);
  };

  const onEntryItemRemoved = (formRemove: (index: number | number[]) => void, index: number) => {
    const updatedSelectedResources = [
      ...selectedResources.slice(0, index),
      ...selectedResources.slice(index + 1),
    ];
    setSelectedResources(updatedSelectedResources);
    formRemove(index);
  };

  const onEntryItemChange = (index: number, field?: 'unit' | 'total') => {
    const currentFormItems: EntryItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const entryItem = currentFormItems[index];

    if (!entryItem) {
      return;
    }

    const updatedPrices = updateEntryPrices(entryItem, field);

    const updatedFormItems: typeof currentFormItems = [
      ...currentFormItems.slice(0, index),
      {
        ...entryItem,
        ...updatedPrices,
        quantity: entryItem.quantity,
      },
      ...currentFormItems.slice(index + 1),
    ];

    formHandler.setFields([{
      name: 'items',
      value: updatedFormItems,
    }]);
  };

  const onResourceSelect = (resource: Resource | undefined, index: number) => {
    if (resource) {
      const updatedSelectedResources = [
        ...selectedResources.slice(0, index),
        { ...resource },
        ...selectedResources.slice(index + 1),
      ];
      setSelectedResources(updatedSelectedResources);
      dispatch(cleanResourcesAction());
      setSearchIndex(undefined);
    }
  };

  return (
    <Modal
      visible={isVisible}
      title="Nova transferência"
      destroyOnClose
      onCancel={internalOnCancel}
      afterClose={internalOnCancel}
      zIndex={90}
      width={720}
      footer={[
        <InlineButton
          key="cancel"
          onClick={internalOnCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={onSubmit}
          disabled={isLoading}
          loading={isLoading}
        >
          Salvar
        </InlineButton>,
      ]}
    >
      <FormContainer
        layout="vertical"
        form={formHandler}
        requiredMark={false}
        onFinish={onSubmit as (val: unknown) => void}
      >
        <BaseFormItem
          label="Data da transferência"
          name="entryDate"
          initialValue={dayjs()}
          rules={[
            {
              required: true,
              message: 'Insira uma data de entrada',
            },
          ]}
        >
          <DatePicker disabled={isLoading} />
        </BaseFormItem>
        <BaseFormItem
          label="Cliente"
          name="client"
          rules={[
            {
              required: true,
              message: 'Nenhum cliente selecionado',
            },
          ]}
        >
          <Select
            placeholder="Selecione um cliente"
            disabled={isLoading}
            showArrow={false}
            showSearch
            onSearch={onClientSearch}
            filterOption={false}
            notFoundContent={<></>}
            dropdownRender={(menu) => (
              <div>
                <RemoteSelectContent>
                  {menu}
                  {(internalLoading || isLoadingClients) && (
                    <RemoteSelectSpinnerContainer>
                      <Spinner />
                    </RemoteSelectSpinnerContainer>
                  )}
                </RemoteSelectContent>
              </div>
            )}
          >
            {clients.map((clientOption) => (
              <Select.Option
                value={clientOption.id}
                key={clientOption.id}
              >
                {clientOption.name}
              </Select.Option>
            ))}
          </Select>
        </BaseFormItem>
        <BaseFormItem
          label="Transferir para"
          name="destinationType"
          initialValue={InventoryTypeEnum.ORGANIZATION}
        >
          <Radio.Group
            disabled={isLoading}
            onChange={(e) => {
              const curOption = e.target.value;
              setDestinationType(curOption);
            }}
          >
            <Radio value={InventoryTypeEnum.ORGANIZATION}>Viveiro</Radio>
            <Radio value={InventoryTypeEnum.CLIENT}>Cliente</Radio>
          </Radio.Group>
        </BaseFormItem>
        <BaseFormItem
          name="destinationClient"
          hidden={destinationType !== InventoryTypeEnum.CLIENT}
          rules={[
            {
              required: destinationType === InventoryTypeEnum.CLIENT,
              message: 'Nenhum cliente selecionado',
            },
          ]}
        >
          <Select
            placeholder="Selecione um cliente"
            disabled={isLoading}
            showArrow={false}
            showSearch
            onSearch={onClientSearch}
            filterOption={false}
            notFoundContent={<></>}
            dropdownRender={(menu) => (
              <div>
                <RemoteSelectContent>
                  {menu}
                  {(internalLoading || isLoadingClients) && (
                    <RemoteSelectSpinnerContainer>
                      <Spinner />
                    </RemoteSelectSpinnerContainer>
                  )}
                </RemoteSelectContent>
              </div>
            )}
          >
            {clients.map((clientOption) => (
              <Select.Option
                value={clientOption.id}
                key={clientOption.id}
              >
                {clientOption.name}
              </Select.Option>
            ))}
          </Select>
        </BaseFormItem>
        <BaseFormItem
          name="destinationOrganization"
          hidden={destinationType !== InventoryTypeEnum.ORGANIZATION}
          rules={[
            {
              required: destinationType === InventoryTypeEnum.ORGANIZATION,
              message: 'Selecione a unidade',
            },
          ]}
        >
          <Select
            disabled={isLoading}
          >
            {organizations
              .map((org) => (
                <Select.Option value={org.id} key={org.id}>
                  {org.name}
                </Select.Option>
              ))}
          </Select>
        </BaseFormItem>
        <h3>Itens</h3>
        <Form.List
          name="items"
          rules={[{
            message: 'Insira pelo menos um item',
            validator: () => {
              const formItems: EntryItemFormData[] = formHandler.getFieldValue('items');
              if (formItems.length > 0) {
                return Promise.resolve();
              }

              return Promise.reject();
            },
          }]}
        >
          {(fields, { add, remove }) => (
            <>
              {fields.map((itemField, index) => (
                <EntryItemFormRow
                  key={itemField.key}
                >
                  <EntryItemInput
                    label="Item"
                    style={{
                      flex: '2',
                    }}
                    name={[itemField.name, 'resource']}
                    validateTrigger="onSubmit"
                    rules={[
                      {
                        required: true,
                        message: 'Selecione um item',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecione um item"
                      disabled={isLoading}
                      showArrow={false}
                      showSearch
                      onSearch={(searchValue) => onResourceSearch(searchValue, index)}
                      filterOption={false}
                      onSelect={(
                        resourceId,
                      ) => onResourceSelect(resourceSearchEntities[resourceId], index)}
                      notFoundContent={<></>}
                      dropdownRender={(menu) => (
                        <div>
                          <RemoteSelectContent>
                            {menu}
                            {(internalLoading || isLoadingResources) && (
                              <RemoteSelectSpinnerContainer>
                                <Spinner />
                              </RemoteSelectSpinnerContainer>
                            )}
                          </RemoteSelectContent>
                        </div>
                      )}
                    >
                      {searchIndex === index
                        ? resourceSearchResult.map((resourceOption) => (
                          <Select.Option
                            value={resourceOption.id}
                            key={resourceOption.id}
                          >
                            {resourceOption.name}
                          </Select.Option>
                        ))
                        : (() => {
                          const resourceOption = selectedResources[index];
                          return resourceOption ? (
                            <Select.Option
                              value={resourceOption.id}
                              key={resourceOption.id}
                            >
                              {resourceOption.name}
                            </Select.Option>
                          ) : null;
                        })()}
                    </Select>
                  </EntryItemInput>
                  <QuantityFormItem
                    label="Quantidade"
                    name={[itemField.name, 'quantity']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma quantidade',
                      },
                    ]}
                  >
                    <Input
                      onChange={() => onEntryItemChange(index)}
                      type="number"
                      disabled={isLoading || !selectedResources[index]}
                      suffix={(() => {
                        const item = selectedResources[index];
                        return item !== undefined ? UnitMapping[item.unit].name : '';
                      })()}
                    />
                  </QuantityFormItem>
                  <PriceFormItem
                    label="Preço Unitário"
                    name={[itemField.name, 'unitPrice']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira um preço',
                      },
                    ]}
                  >
                    <Input
                      onChange={() => onEntryItemChange(index, 'unit')}
                      type="number"
                      disabled={isLoading || !selectedResources[index]}
                      prefix="R$"
                    />
                  </PriceFormItem>
                  <PriceFormItem
                    label="Valor"
                    name={[itemField.name, 'totalPrice']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira um preço',
                      },
                    ]}
                  >
                    <Input
                      onChange={() => onEntryItemChange(index, 'total')}
                      type="number"
                      disabled={isLoading || !selectedResources[index]}
                      prefix="R$"
                    />
                  </PriceFormItem>
                  <RemoveEntryItemButton onClick={() => onEntryItemRemoved(remove, index)}>
                    <RemoveEntryItemIcon />
                  </RemoveEntryItemButton>
                </EntryItemFormRow>
              ))}
              <AddEntryItemButton
                type="dashed"
                onClick={() => onEntryItemAdded(add, fields.length)}
                icon={<PlusOutlined size={14} />}
              >
                Adicionar item
              </AddEntryItemButton>
            </>
          )}
        </Form.List>
        <NotesInline
          label="Observações"
          name="notes"
        >
          <Input.TextArea disabled={isLoading} rows={5} />
        </NotesInline>
      </FormContainer>
    </Modal>
  );
};

export default EntryTransferModal;
